import { useState } from 'react'
import { daysBetween, type ISODate, type Task } from '@app/shared'

import { SkeletonRows } from '../Card'
import { CountRoll } from '../motion/CountRoll'
import { Recur, Tick } from './icons'
import { DONE_LOG_KEPT_DAYS, doneDays, doneThisWeek, recurrenceWord, SOON_DAYS } from './derive'
import { DayEyebrow, Unavailable } from './parts'

/**
 * THE DONE LOG — 296px, the right of the three columns (README §7).
 *
 * This is the entire reward system. Finishing a task moves the row here and that is all that
 * happens: no ring, no streak, no percentage, no confetti. The archive is the point — what you
 * did on Tuesday is more useful than a number that says you are 64% of something.
 *
 * The header count and the list are two different questions on purpose. "11 this week" is the
 * last seven days; the list is everything still kept. The footer says how long that is, and
 * `All` swaps the list between the two so the difference is never hidden.
 */
export function DoneLog({
  tasks,
  today,
  isPending,
  error,
}: {
  tasks: Task[] | undefined
  today: ISODate
  isPending: boolean
  error: unknown
}) {
  const [showAll, setShowAll] = useState(false)

  const all = doneDays(tasks ?? [], today)
  const week = all.filter((d) => daysBetween(d.date, today) <= SOON_DAYS)
  const days = showAll ? all : week
  const thisWeek = doneThisWeek(tasks ?? [], today)
  const older = all.length - week.length

  return (
    <section className="flex w-[296px] flex-none flex-col gap-3 overflow-hidden rounded-context border border-line bg-surface px-[17px] py-[15px] shadow-card">
      <header className="flex flex-none items-center gap-2">
        <Tick size={15} className="flex-none text-success-dot" />
        <h2 className="text-[13px] font-semibold leading-none">Done log</h2>
        {/* 13b's tail. The number rolling up by one IS the reward for finishing a task — the
            brief's words, and the reason this screen has no ring and no streak. */}
        <span
          className="ml-auto text-[11px] font-normal leading-none text-microcopy"
          title="Reminders completed in the last seven days. The list below keeps every completion for 90 days."
        >
          {tasks && <CountRoll value={thisWeek} suffix="this week" lineHeight={13} />}
        </span>
      </header>

      {isPending && <SkeletonRows rows={5} />}
      <Unavailable error={error} />

      {tasks && days.length === 0 && (
        <p className="text-[11.5px] leading-[1.45] text-microcopy text-pretty">
          {showAll
            ? 'Nothing has been finished yet. Completions are kept here for 90 days.'
            : 'Nothing finished in the last seven days.'}
        </p>
      )}

      <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto">
        {days.map((day) => (
          <div key={day.date} className="flex flex-none flex-col gap-[6px]">
            <DayEyebrow>{day.label}</DayEyebrow>
            {day.tasks.map((task) => (
              <DoneRow key={task.id} task={task} />
            ))}
          </div>
        ))}
      </div>

      <footer className="mt-auto flex flex-none items-center gap-2 border-t border-fill pt-[11px]">
        <p className="flex-1 text-[11px] leading-[1.4] text-microcopy text-pretty">
          Kept for {DONE_LOG_KEPT_DAYS} days, then archived to the journal.
        </p>
        {/* Real, and local: the whole kept window is already in hand, so this filters rather
            than fetches. It is only offered when there is actually something older to show —
            an `All` that reveals nothing is a promise the list cannot keep. */}
        {older > 0 && (
          <button
            type="button"
            onClick={() => setShowAll((v) => !v)}
            title={
              showAll
                ? 'Show only the last seven days.'
                : `Show all ${all.reduce((n, d) => n + d.tasks.length, 0)} kept completions, including ${older} day${older === 1 ? '' : 's'} older than a week.`
            }
            className="flex-none text-[11px] font-semibold leading-none text-success-dot transition-colors duration-[var(--dur-state)] ease-[var(--ease-ink)] hover:text-success"
          >
            {showAll ? 'This week' : 'All'}
          </button>
        )}
      </footer>
    </section>
  )
}

function DoneRow({ task }: { task: Task }) {
  const recurring = recurrenceWord(task.rrule) !== null
  return (
    <div className="flex items-center gap-[9px]">
      {/* The same status green as the Canvas-synced dot and the transcribed chip. One meaning,
          one token: "this finished". */}
      <Tick size={13} strokeWidth={2.2} className="flex-none text-live-dot" />
      {/* Wraps rather than truncates — 'Update the waitlist number from CalCentral' is exactly
          the length that would lose its last word at 296px, and the last word is the answer. */}
      <span className="min-w-0 flex-1 text-[12px] leading-[1.35] text-muted line-through text-pretty">
        {task.title}
      </span>
      {/* The recurring one keeps a stamp so a repeated chore does not read as a duplicate. */}
      {recurring && (
        <Recur size={10} strokeWidth={2.2} heads={false} className="flex-none text-faintest-2" />
      )}
    </div>
  )
}
